import {getIsVRActive} from './heliodor_xr';
import {setScreenshotData, getScreenshotData} from './logics/dataOps';
import {
  hel_canvas_ResetToggleDefault,
  hel_canvas_SetGUIShow,
  hel_canvas_ToggleChange
} from './logics/gui_caller';
import {getMainCanvasDOMElement} from './logics/mainCanvasDOM';
import {hel_DispatchEvent} from './logics/moduleCaller';
import {hel_isiPhone, hel_isLangJa} from './logics/platform';
import {hel_send_notify_message} from './logics/shared';
import {hel_video_is_paused} from './logics/video_callee';

const convert_jpeg_to_png = async (jpegDataURL: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    //
    const img = new Image();
    img.src = jpegDataURL;

    //
    img.onload = () => {
      // jpeg画像をCanvasに描画する
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = img.width;
      tmpCanvas.height = img.height;

      const ctx = tmpCanvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);

      // 描画に使ったCanvasをpng形式のblobに変換する
      tmpCanvas.toBlob((blob: Blob | null) => {
        tmpCanvas.remove();

        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('[Error] Failed to convert jpeg to png.'));
        }
      }, 'image/png');
    };
  });
};

const hel_share_file = async (sharetitle: string, sharetext: string, sharefile: File) => {
  if (window.navigator.share === undefined) {
    // navigator.shareはセキュリティ対策でhttps環境でしか使用できない
    console.error('[Error] navigator.share is undefined. It is available when using HTTPS.');
    return false;
  }

  const result = await window.navigator
    .share({
      title: sharetitle,
      text: sharetext,
      files: [sharefile]
    })
    .then(() => {
      return true;
    })
    .catch((error) => {
      console.error(error);

      return false;
    });

  return result;
};

/**
 * @param url
 */
const hel_openPage = (url: string) => {
  if (EnabledExternalCheck() && IsWorldLink(url)) {
    // 遷移先でも外部リンクチェックをしたいのでifexternalフラグを引き継ぐ
    url = hel_set_QueryParam('ifexternal', '1', url);
  }

  // iOSでは非同期処理中にopenが使用できないのでその時はページ遷移を行う
  if (!window.open(url, '_blank')) {
    window.location.href = url;
  } else if (getIsVRActive()) {
    // WebXRモード中はユーザーが裏でタブが開かれた事を気づけないためトーストで通知する
    const ja = 'リンクを新しいタブで開きました。\nHomeボタン、またはWebXR終了で確認できます。';
    const en = 'Link opened in a new tab.\nCheck using Home button or exit WebXR.';
    const message = hel_isLangJa() ? ja : en;
    hel_send_notify_message(0, message, 5.0, '', '');
  }
};

/**
 * Cppから参照
 */
export const hel_get_QueryParam = (queryName: string, srcURL: string = ''): string => {
  if (srcURL === undefined || srcURL === null || srcURL === '') {
    srcURL = document.location.href;
  }

  const params = new URL(srcURL).searchParams;

  const QueryParam = params.get(queryName);

  if (QueryParam === undefined || QueryParam === null) {
    return '';
  }

  return QueryParam;
};

const hel_set_QueryParam = (queryName: string, queryValue: string, srcURL: string): string => {
  const urlObj = new URL(srcURL);

  urlObj.searchParams.set(queryName, queryValue);

  return urlObj.href;
};

const EnabledExternalCheck = (): boolean => {
  // 外部リンクチェックを行うかどうか
  const ifexternal = hel_get_QueryParam('ifexternal', ''); // 現在のインゲームURLからフラグ取得
  return ifexternal === '1';
};

const IsWorldLink = (url: string): boolean => {
  // 遷移先がワールドかどうか
  const worldid = hel_get_QueryParam('worldid', url); // 遷移先URLからフラグ取得
  return worldid !== '';
};

// screenshot
const hel_share_screenshot = async (sharetitle: string, sharetext: string) => {
  const ssDataURL = getScreenshotData();

  const blob = await (await fetch(ssDataURL)).blob();
  if (blob === undefined || blob === null) return false;

  const paramList = blob.type.split('/');
  let extention = 'png';
  if (paramList[1] !== undefined) extention = paramList[1];

  const today = new Date();

  const fileName =
    today.getFullYear() +
    ('00' + (today.getMonth() + 1)).slice(-2) +
    ('00' + today.getDate()).slice(-2) +
    '_' +
    ('00' + today.getHours()).slice(-2) +
    ('00' + today.getMinutes()).slice(-2) +
    ('00' + today.getSeconds()).slice(-2) +
    '.' +
    extention;

  const file = new File([blob], fileName, {type: blob.type});

  return await hel_share_file(sharetitle, sharetext, file);
};

Object.assign(globalThis, {
  /**
   * @remarks
   * C++, heliodor.jsから呼出があるためglobal関数
   *
   * @param visible
   */
  hel_video_set_movieviewer_ui: (visible: boolean) => {
    // ※スライダーの表示・非表示ははCPPで制御してます。
    // ※スライダーも同時に表示制御したい場合は、 heliodor_front.js の
    // hel_video_set_movieviewer_ui_all() を使う事。

    // console.log("*** hel_video_set_movieviewer_ui(" + visible + ")");

    // 停止中？
    const paused = hel_video_is_paused();

    // --------------------------------------------
    // movieviewer/_po.json の Toggle を設定する。

    // (1)一旦デフォルト値にする（＝movieviewer/_po.jsonのToggle値にする）
    hel_canvas_ResetToggleDefault('IsVisible_VideoPlayerCurrentTime'); // true
    hel_canvas_ResetToggleDefault('IsVisible_VideoPlayerTotalTime'); // true
    hel_canvas_ResetToggleDefault('IsVisible_hud_movie_play'); // false
    hel_canvas_ResetToggleDefault('IsVisible_hud_movie_pause'); // true
    hel_canvas_ResetToggleDefault('IsVisible_hud_movie_forward'); // true
    hel_canvas_ResetToggleDefault('IsVisible_hud_movie_rewind'); // true
    // (2)再設定
    if (visible) {
      // 表示させる
      if (paused) {
        // ポーズ中ならデフォルト値を反転
        hel_canvas_ToggleChange('IsVisible_hud_movie_play'); // false => true
        hel_canvas_ToggleChange('IsVisible_hud_movie_pause'); // true => false
      }
    } else {
      // 表示させない
      hel_canvas_ToggleChange('IsVisible_VideoPlayerCurrentTime'); // true => false
      hel_canvas_ToggleChange('IsVisible_VideoPlayerTotalTime'); // true => false
      hel_canvas_ToggleChange('IsVisible_hud_movie_pause'); // true => false
      hel_canvas_ToggleChange('IsVisible_hud_movie_forward'); // true => false
      hel_canvas_ToggleChange('IsVisible_hud_movie_rewind'); // true => false
    }
    // -----------------------------------------

    // 横画面
    let layout_name = 'movieviewer';
    hel_canvas_SetGUIShow(layout_name, 'VideoPlayerCurrentTime', visible);
    hel_canvas_SetGUIShow(layout_name, 'VideoPlayerTotalTime', visible);
    if (paused) {
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_play', visible);
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_pause', false);
    } else {
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_play', false);
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_pause', visible);
    }
    hel_canvas_SetGUIShow(layout_name, 'hud_movie_forward', visible);
    hel_canvas_SetGUIShow(layout_name, 'hud_movie_rewind', visible);

    // 縦画面
    layout_name = 'movieviewer_po';
    hel_canvas_SetGUIShow(layout_name, 'VideoPlayerCurrentTime', visible);
    hel_canvas_SetGUIShow(layout_name, 'VideoPlayerTotalTime', visible);
    if (paused) {
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_play', visible);
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_pause', false);
    } else {
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_play', false);
      hel_canvas_SetGUIShow(layout_name, 'hud_movie_pause', visible);
    }
    hel_canvas_SetGUIShow(layout_name, 'hud_movie_forward', visible);
    hel_canvas_SetGUIShow(layout_name, 'hud_movie_rewind', visible);
  },

  /**
   * @remarks
   * C++から呼び出される
   * スクリーンショット
   */
  hel_screenshot: () => {
    const mainCanvas = getMainCanvasDOMElement();
    if (mainCanvas === undefined) {
      throw new Error('[ hel_screenshot ] mainCanvas is not assigned. noop');
    }

    // dataURLの一時保持としてデータSET
    setScreenshotData(mainCanvas.toDataURL('image/jpeg'));

    // iOSは直接ダウンロードができないので共有ダイアログを開くためのイベントを発火する
    // AndroidではLINEミニアプリで共有ダイアログを使うことができないので実行しない
    if (hel_isiPhone() && mainCanvas !== void 0) {
      hel_DispatchEvent('HEL_SCREENSHOT', '');
      return;
    }

    // その他のデバイスでのスクショ
    mainCanvas.toBlob((blob: Blob | null) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob!);

      const today = new Date();

      a.download =
        '' +
        today.getFullYear() +
        ('00' + (today.getMonth() + 1)).slice(-2) +
        ('00' + today.getDate()).slice(-2) +
        '_' +
        ('00' + today.getHours()).slice(-2) +
        ('00' + today.getMinutes()).slice(-2) +
        ('00' + today.getSeconds()).slice(-2) +
        '.png';

      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
  },

  /**
   * HSから参照
   */
  hel_share_screenshot,

  hel_share_file,

  /**
   * HSから参照
   */
  hel_copy_screenshot_to_clipboard: async () => {
    const ssDataURL = getScreenshotData();

    // jpegのスクリーンショットをpngに変換した上でクリップボードにコピーする
    // localstorageにjpegを保存しているのはpngだとシーンによっては最大容量の5MBを超えてQuotaExceededErrorが出てしまうため
    // しかしclipboard.writeはpngしか対応していないため変換する必要がある
    // また、clipboard.writeよりも前に非同期処理があるとMac + SafariでNotAllowedErrorが出てしまうことにも注意
    const result = await window.navigator.clipboard
      .write([
        new ClipboardItem({
          'image/png': convert_jpeg_to_png(ssDataURL)
        })
      ])
      .then(() => {
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });

    return result;
  },

  // URL
  /**
   * C++から参照
   */
  hel_openPage,

  /**
   * C++とCanvasHSから参照
   * @param url
   */
  hel_transitionToPage: (url: string) => {
    if (EnabledExternalCheck()) {
      // 外部リンクチェックが有効で遷移先がワールド以外の外部リンクなら強制的に別タブ遷移する
      // iframeで外部リンクを同タブで開こうとすると失敗するのでその対策
      if (!IsWorldLink(url)) {
        hel_openPage(url);
        return;
      }

      // 遷移先でも外部リンクチェックをしたいのでifexternalフラグを引き継ぐ
      url = hel_set_QueryParam('ifexternal', '1', url);
    }

    // ページ遷移
    window.location.href = url;
  },

  /**
   * Cppから参照
   */
  hel_get_QueryParam,

  /**
   * HS, TSから参照
   */
  hel_set_QueryParam,

  /**
   * HeliScriptから参照
   */
  hel_set_HTMLElement_value: (elementName: string, value: string) => {
    const element = document.getElementById(elementName) as HTMLInputElement;
    if (element !== undefined && element != null) {
      element.value = value;
    }
  },

  /**
   * HeliScriptから参照
   */
  hel_get_HTMLElement_value: (elementName: string): string => {
    const element = document.getElementById(elementName) as HTMLInputElement;
    if (element !== undefined && element != null) {
      const value = element.value;
      return value !== undefined && value !== null ? value : '';
    }

    return '';
  },

  /**
   * UUID
   * HeliScriptから参照
   */
  hel_get_randomUUID: (): string => {
    return window.crypto.randomUUID();
  }
});

// ScreenShot Class
class ModalSS {
  private container: HTMLElement | null;
  private btnOpenSS: HTMLElement | null;
  private btnClose: HTMLElement | null;

  constructor() {
    //
    this.container = document.querySelector('#__ss_modal');

    //
    this.btnOpenSS = document.querySelector('#__ss_modal-dialog-btn_open_ss');
    if (this.btnOpenSS !== null) {
      this.btnOpenSS.addEventListener('click', () => {
        this.hide();
      });
    }

    //
    this.btnClose = document.querySelector('#__ss_modal-dialog-btn_close');
    if (this.btnClose !== null) {
      this.btnClose.addEventListener('click', () => {
        this.hide();
      });
    }
  }
  show() {
    if (this.container !== null) this.container.style.visibility = 'visible';
  }
  hide() {
    if (this.container !== null) this.container.style.visibility = 'hidden';
  }
}

const modalSS = new ModalSS();
